import { Suspense, lazy } from "react";

import { Spin } from "antd";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import "./App.css";
import { notAuth, withAuth } from "./auth/authGuard";
import MainLayout from "./components/MainLayout";
import Routers from "./constants/routers";

const ProtectedMainLayout = withAuth(MainLayout);

// Not Auth
const LoginPage = notAuth(lazy(() => import("./pages/Login")));
const HealthCheckPage = notAuth(lazy(() => import("./pages/HealthCheck")));
const MaintenancePage = notAuth(lazy(() => import("./pages/MaintenancePage")));
const ForgotPasswordPage = notAuth(lazy(() => import("./pages/ForgotPasswordPage")));
const ResetPasswordPage = notAuth(lazy(() => import("./pages/ResetPasswordPage")));
const SmsVerificationPage = lazy(() => import("./pages/SmsVerification"));

// With Auth
const DashboardPage = withAuth(lazy(() => import("./pages/Dashboard")));
const VariableCostRegisterPage = withAuth(lazy(() => import("./pages/VariableCostRegister")));
const ApplicationCreatePage = withAuth(lazy(() => import("./pages/ApplicationCreate")));
const ApplicationReviewPage = withAuth(lazy(() => import("./pages/ApplicationReviewPage")));
const BrokerAccessPage = withAuth(lazy(() => import("./pages/BrokerAccessPage")));
const ContractsPage = withAuth(lazy(() => import("./pages/Contracts")));
const ContractsDetailPage = withAuth(lazy(() => import("./pages/Contracts/Detail")));
const GuaraUpdateConfirmPage = withAuth(lazy(() => import("./pages/GuaraUpdateConfirm")));
const NoticePage = withAuth(lazy(() => import("./pages/Notice")));
const SubrogationPaymentPage = withAuth(lazy(() => import("./pages/SubrogationPayment")));
const SubrogationPaymentCreatePage = withAuth(lazy(() => import("./pages/SubrogationPayment/Create")));
const SubrogationPaymentDetailPage = withAuth(lazy(() => import("./pages/SubrogationPayment/Detail")));
const InclusiveContractApplicationPage = withAuth(lazy(() => import("./pages/InclusiveContractApplication")));
const InclusiveContractDetailPage = withAuth(
  lazy(() => import("./pages/InclusiveContractApplication/InclusiveContractDetail"))
);
const InclusiveContractCreate = withAuth(
  lazy(() => import("./pages/InclusiveContractApplication/InclusiveContractCreate"))
);
const ReceivedInvoicePage = withAuth(lazy(() => import("./pages/ReceivedInvoice")));
const CancellationRegisterPage = withAuth(lazy(() => import("./pages/CancellationRegister")));
const BadRequestPage = withAuth(lazy(() => import("./pages/ErrorPage/BadRequest")));
const ForbiddenPage = withAuth(lazy(() => import("./pages/ErrorPage/Forbidden")));
const NotFoundPage = withAuth(lazy(() => import("./pages/ErrorPage/NotFound")));
const InitialGuaranteeFeePage = withAuth(lazy(() => import("./pages/InitialGuaranteeFeePage")));

const ChangePasswordPage = withAuth(lazy(() => import("./pages/ChangePassword")));
const SettingsPage = withAuth(lazy(() => import("./pages/Settings")));
const ShopRegistrationApplyPage = withAuth(lazy(() => import("./pages/ShopRegistrationApply")));
const BrokerProfileSetupPage = withAuth(
  lazy(() => import("./pages/BrokerProfileSetup")),
  {
    allowIncompleteBrokerProfile: true
  }
);

const routerBasename = process.env.PUBLIC_URL || undefined;

function Router() {
  return (
    <>
      <BrowserRouter
        basename={routerBasename}
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <Suspense fallback={<Spin fullscreen />}>
          <Routes>
            <Route path={Routers.Login} element={<LoginPage />} />
            <Route path={Routers.ForgotPassword} element={<ForgotPasswordPage />} />
            <Route path={Routers.ResetPassword} element={<ResetPasswordPage />} />
            <Route path={Routers.HealthCheck} element={<HealthCheckPage />} />
            <Route path={Routers.Maintenance} element={<MaintenancePage />} />
            <Route path={Routers.SmsVerification} element={<SmsVerificationPage />} />

            <Route path={Routers.Dashboard} element={<DashboardPage />} />
            <Route path={Routers.ChangePassword} element={<ChangePasswordPage />} />
            <Route path={Routers.BrokerProfileSetup} element={<BrokerProfileSetupPage />} />

            <Route element={<ProtectedMainLayout />}>
              <Route path={Routers.VariableCostRegister} element={<VariableCostRegisterPage />} />
              <Route path={Routers.ApplicationCreate} element={<ApplicationCreatePage />} />
              <Route path={Routers.ApplicationCreate + "/:contractCode"} element={<ApplicationCreatePage />} />
              <Route path={Routers.ApplicationReview} element={<ApplicationReviewPage />} />
              <Route path={Routers.BrokerAccess} element={<BrokerAccessPage />} />
              <Route path={Routers.ContractsList} element={<ContractsPage />} />
              <Route path={Routers.ContractsDetail + "/:contractCode"} element={<ContractsDetailPage />} />
              <Route path={Routers.GuaraUpdateConfirm} element={<GuaraUpdateConfirmPage />} />
              <Route path={Routers.Notice} element={<NoticePage />} />
              <Route path={Routers.SubrogationPayment} element={<SubrogationPaymentPage />} />
              <Route path={Routers.SubrogationPayment + "/create"} element={<SubrogationPaymentCreatePage />} />
              <Route
                path={Routers.SubrogationPayment + "/:subrogationPaymentCode"}
                element={<SubrogationPaymentDetailPage />}
              />
              <Route path={Routers.InclusiveContractApplication} element={<InclusiveContractApplicationPage />} />
              <Route
                path={Routers.InclusiveContractDetail + "/:contractCode"}
                element={<InclusiveContractDetailPage />}
              />
              <Route path={Routers.InclusiveContractCreate} element={<InclusiveContractCreate />} />
              <Route path={Routers.InclusiveContractCreate + "/:contractCode"} element={<InclusiveContractCreate />} />
              <Route path={Routers.ReceivedInvoice} element={<ReceivedInvoicePage />} />
              <Route path={Routers.CancellationRegister} element={<CancellationRegisterPage />} />
              <Route path={Routers.BadRequest} element={<BadRequestPage />} />
              <Route path={Routers.Forbidden} element={<ForbiddenPage />} />
              <Route path={Routers.NotFound} element={<NotFoundPage />} />
              <Route path={Routers.InitialGuaranteeFee} element={<InitialGuaranteeFeePage />} />
              <Route path={Routers.Settings} element={<SettingsPage />} />
              <Route path={`${Routers.ShopRegistrationApplies}/new`} element={<ShopRegistrationApplyPage />} />
              <Route path={`${Routers.ShopRegistrationApplies}/:applyId`} element={<ShopRegistrationApplyPage />} />
            </Route>
            <Route path={Routers.All} element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
}

function App() {
  return (
    <>
      <Router />
    </>
  );
}

export default App;
